<template>
  <div>
    <div>
      <b-table
        id="table1"
        ref="amendmentsTable"
        :class="editing ? 'd-none' : ''"
        striped
        borderless
        hover
        small
        sticky-header
        responsive="sm"
        :items="amendments"
        :fields="fields"
        :busy="loading"
        show-empty
        :empty-text="emptyText">
        <template v-slot:cell(actions)="row">
          <div>
            <b-button
              variant="link"
              size="sm"
              :disabled="editing || loading || busy"
              @click="download(row.item)"
              aria-label="Download this amendment">
              Download
            </b-button>
          </div>
        </template>
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>&nbsp;
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
    </div>
    <div>
      <b-table
        id="table2"
        ref="scratchAmendmentsTable"
        :class="editing ? '' : 'd-none'"
        striped
        borderless
        hover
        small
        sticky-header
        responsive="sm"
        primary-key="id"
        :items="scratchAttachments"
        :fields="scratchFields"
        :busy="loading"
        show-empty
        empty-text="No amendments have been staged for upload.">
        <template v-slot:cell(actions)="row">
          <div>
            <div v-if="!row.item.isLoaded">
              <b-progress
                :value="row.item.loadingValue"
                max="100"
                height="3px"
                animated>
              </b-progress>
            </div>
            <div v-else class="amendment-tbl-btns">
              <b-button
                variant="link"
                size="sm"
                class="text-danger"
                :disabled="!editing || loading || busy"
                @click="removeScratchAmendment(row.item)"
                aria-label="Remove this amendment from staging">
                Remove
              </b-button>
              <span class="d-none d-md-inline-block"> | </span>
              <b-button
                variant="link"
                size="sm"
                :disabled="!editing || loading || busy"
                @click="downloadScratchAmendment(row.item)"
                aria-label="Download this amendment from staging">
                Download
              </b-button>
            </div>
          </div>
        </template>
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>&nbsp;
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
    </div>

    <hr v-if="isNotifierUser" class="border-dark">

    <div v-if="editing" class="d-flex flex-row flex-grow-1">
      <form
        ref="amendmentsForm"
        class="d-flex flex-column flex-grow-1"
        enctype="multipart/form-data"
        novalidate>
        <div class="dropzone my-3">
          <input
            ref="amendmentFiles"
            class="input-file"
            type="file"
            multiple
            name="amendments"
            :disabled="!editing || loading || busy"
            @change="filesChange($event.target.files)"
            :accept="acceptedFileTypes">
          <p v-if="saving">
            Saving {{ scratchAttachments.length }} file(s)...
          </p>
          <p v-else>
            Drag your file(s) here to begin<br> or click to browse
            <br><br>Be sure to click 'Save' when done uploading
          </p>
        </div>
        <div class="amendment-btns d-flex justify-content-end w-100 w-md-auto mt-2">
          <b-button
            pill
            variant="danger"
            class="my-3 mt-md-auto mr-md-3 w-100 w-md-auto"
            :disabled="!editing || busy"
            @click="cancel()"
            aria-label="Cancel adding amendments">
            Cancel
          </b-button>
          <b-button
            pill
            variant="success"
            class="my-3 mt-md-auto w-100 w-md-auto"
            :disabled="!editing || loading || busy"
            @click="save()"
            aria-label="Save amendments">
            Save
          </b-button>
        </div>
      </form>
    </div>
    <div v-else class="d-flex flex-row flex-grow-1">
      <div
        v-if="(mfsAdmin || isNotifierUser) && active"
        class="d-flex justify-content-end w-100 w-md-auto mt-2">
        <b-button
          class="mb-3 w-100 w-md-auto"
          pill
          @click="edit()"
          aria-label="Add amendments"
          :disabled="editing || loading || busy">
          Add Amendments
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';

export default {
  name: 'UploadAmendmentsForm',
  async created() {
    if (this.isExtraSmall) {
      // move actionField in front of basic fields
      this.unshiftActions(this.fields);
      this.unshiftActions(this.scratchFields);
    }
    await this.$store.dispatch('Notification/getAmendments');
  },
  data() {
    return {
      acceptedFileTypes: [
        'image/*',
        '.pdf',
        '.doc',
        '.docx',
        '.txt',
        '.xlsx',
        '.csv',
        '.xml',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].join(','),
      fields: [
        {
          key: 'fileName',
          label: 'File Name',
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'uploadedBy',
          label: 'Uploaded By',
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'uploadDate',
          label: 'Uploaded On',
          sortable: true,
          tdClass: 'align-middle',
          formatter: dateTime => moment.utc(dateTime).format('MM/DD/YYYY'),
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          tdClass: 'align-middle',
        },
      ],
      scratchFields: [
        { key: 'fileName', label: 'File Name', tdClass: 'align-middle' },
        {
          key: 'size',
          label: 'Size',
          tdClass: 'align-middle',
          formatter: (bytes) => {
            // eslint-disable-next-line
            if (isNaN(parseFloat('' + bytes)) || !isFinite(bytes)) {
              return '-';
            }

            if (bytes <= 0) {
              return '0';
            }
            const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
            const calc1 = Math.floor(Math.log(bytes) / Math.log(1024));
            const calculated = (bytes / (1024 ** Math.floor(calc1))).toFixed(1);
            return `${calculated} ${units[calc1]}`;
          },
        },
        { key: 'actions', label: 'Actions', tdClass: 'align-lg-middle' },
      ],
    };
  },
  computed: {
    ...mapState(['isExtraSmall']),
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
      loading: state => state.amendmentsLoading,
      busy: state => state.amendmentsBusy,
      saving: state => state.amendmentsSaving,
      editing: state => state.amendmentsEditing,
      amendments: state => state.amendments,
      amendmentsError: state => state.amendmentsError,
      scratchSessionId: state => state.scratchSessionId,
      scratchAttachments: state => state.scratchAttachments,
    }),
    ...mapGetters('Notification', [
      'active',
      'closed',
      'scheduled',
    ]),
    ...mapGetters('User', [
      'mfsAdmin',
    ]),
    isNotifierUser() {
      return this.notification.createdBy === this.user.id;
    },
    emptyText() {
      return this.closed
        ? 'No amendments were uploaded to this notification.'
        : 'No amendments have been uploaded.';
    },
  },
  watch: {
    amendments() {
      this.$refs.amendmentsTable.refresh();
    },
    scratchAttachments() {
      this.$refs.scratchAmendmentsTable.refresh();
    },
    isExtraSmall() {
      if (this.isExtraSmall) {
        // move actionField in front of fields
        this.unshiftActions(this.fields);
        this.unshiftActions(this.scratchFields);
      } else {
        // move actionField to end of fields
        this.shiftActions(this.fields);
        this.shiftActions(this.scratchFields);
      }
    },
  },
  methods: {
    unshiftActions(fields) {
      const actionField = fields.pop();
      fields.unshift(actionField);
    },
    shiftActions(fields) {
      const actionField = fields.shift();
      fields.push(actionField);
    },
    edit() {
      if (this.editing || this.loading || this.busy) { return; }
      this.$store.dispatch('Notification/initAmendmentScratchSession', this.notification.id);
    },
    cancel() {
      if (!this.editing || this.busy) { return; }
      this.$store.dispatch('Notification/clearAmendmentScratchSession');
    },
    save() {
      if (!this.editing || this.loading || this.busy) { return; }
      this.$store.dispatch('Notification/saveAmendments');
    },
    download(item) {
      if (this.editing || this.loading || this.busy) { return; }
      this.$store.dispatch('Notification/downloadAmendment', item.id);
    },
    filesChange(fileList) {
      if (!fileList.length) return;
      this.$store.dispatch('Notification/uploadAmendmentsToScratchSession', fileList);
    },
    removeScratchAmendment(item) {
      if (!this.editing || this.loading || this.busy) { return; }
      this.$refs.amendmentFiles.value = null;
      this.$store.dispatch('Notification/deleteScratchAmendment', item.id);
    },
    downloadScratchAmendment(item) {
      if (!this.editing || this.loading || this.busy) { return; }
      this.$store.dispatch('Notification/downloadScratchAmendment', item.id);
    },
  },
};
</script>
